import { TransactionsImportStatus } from "@common/types/financialDocument";
import { isImporterTransactionsQueue } from "@common/helpers/financialDocument";
import { FinancialAccount } from "@common/types/financialAccount";
import { ImporterDocumentRow, ImporterSort } from "./types";
import {
    documentBeingProcessed,
    isImportRunning,
    getDisplayedErrors,
    ImporterDisplayErrorKind,
    IMPORTER_ERROR_PRIORITY,
} from "./helpers";

interface StatusSortContext {
    importerQueue: string;
    reconciliedFinancialAccount?: FinancialAccount;
}

function compareStrings(
    a: string | null | undefined,
    b: string | null | undefined,
    asc: boolean,
): number {
    const aEmpty = !a;
    const bEmpty = !b;

    if (aEmpty && bEmpty) {
        return 0;
    }
    if (aEmpty) {
        return 1;
    }
    if (bEmpty) {
        return -1;
    }

    const cmp = a!.localeCompare(b!);
    return asc ? cmp : -cmp;
}

function compareNumbers(
    a: number | null | undefined,
    b: number | null | undefined,
    asc: boolean,
): number {
    const aValid = a ?? null;
    const bValid = b ?? null;

    if (aValid === null && bValid === null) {
        return 0;
    }
    if (aValid === null) {
        return 1;
    }
    if (bValid === null) {
        return -1;
    }

    const cmp = aValid - bValid;
    return asc ? cmp : -cmp;
}

function compareDates(
    a: Date | string | null | undefined,
    b: Date | string | null | undefined,
    asc: boolean,
): number {
    const aTime = a ? new Date(a).getTime() : null;
    const bTime = b ? new Date(b).getTime() : null;
    const aValid = Number.isNaN(aTime) ? null : aTime;
    const bValid = Number.isNaN(bTime) ? null : bTime;

    return compareNumbers(aValid, bValid, asc);
}

function getRowInstitutionName(row: ImporterDocumentRow): string | undefined {
    const doc = row.financialDocument;
    return (
        doc?.financialAccount?.institutionName ??
        doc?.accountInfoExtraction?.institutionName ??
        undefined
    );
}

function getRowAccountType(row: ImporterDocumentRow): string | undefined {
    const doc = row.financialDocument;
    return (
        doc?.financialAccount?.financialType ??
        doc?.accountInfoExtraction?.accountType ??
        undefined
    );
}

enum StatusSortGroup {
    NO_DOCUMENT,
    PROCESSING,
    FAILED,
    ERRORS,
    READY,
    IMPORTING,
    IMPORTED,
}

const PRIORITY_SCALE = 100;

/**
 * Mirrors the conditional chain in ImporterDocumentStatus so sorting
 * reflects the status the user actually sees, not the raw DB enum.
 * Within the errors group, sub-sorts by IMPORTER_ERROR_PRIORITY so
 * more critical errors (e.g. password-protected) appear first.
 *
 * IMPORTANT: Keep in sync with ImporterDocumentStatus.tsx render logic.
 */
function getStatusSortPriority(
    row: ImporterDocumentRow,
    context: StatusSortContext,
): number {
    const doc = row.financialDocument;

    if (!doc) {
        return StatusSortGroup.NO_DOCUMENT * PRIORITY_SCALE;
    }

    if (documentBeingProcessed(doc)) {
        return StatusSortGroup.PROCESSING * PRIORITY_SCALE;
    }

    if (
        doc.transactionsImportStatus === TransactionsImportStatus.FINISHED &&
        isImporterTransactionsQueue(context.importerQueue)
    ) {
        return StatusSortGroup.IMPORTED * PRIORITY_SCALE;
    }

    if (isImportRunning(doc)) {
        return StatusSortGroup.IMPORTING * PRIORITY_SCALE;
    }

    if (doc.transactionsImportStatus === TransactionsImportStatus.FAILED) {
        return StatusSortGroup.FAILED * PRIORITY_SCALE;
    }

    const displayedErrors = getDisplayedErrors(
        doc,
        context.reconciliedFinancialAccount,
    );
    if (displayedErrors.length > 0) {
        const topError = displayedErrors[0];
        const errorSubPriority =
            topError.kind === ImporterDisplayErrorKind.IMPORTER_EXTRACTION
                ? IMPORTER_ERROR_PRIORITY[topError.error]
                : PRIORITY_SCALE - 1;
        return StatusSortGroup.ERRORS * PRIORITY_SCALE + errorSubPriority;
    }

    return StatusSortGroup.READY * PRIORITY_SCALE;
}

interface SortDocumentRowsOptions extends StatusSortContext {
    rows: ImporterDocumentRow[];
    sort: ImporterSort | undefined;
}

export function sortDocumentRows({
    rows,
    sort,
    ...context
}: SortDocumentRowsOptions): ImporterDocumentRow[] {
    if (!sort) {
        return rows;
    }

    return [...rows].sort((a, b) => {
        const aDoc = a.financialDocument;
        const bDoc = b.financialDocument;

        switch (sort) {
            case ImporterSort.FILE_NAME_ASC:
            case ImporterSort.FILE_NAME_DESC:
                return compareStrings(
                    aDoc?.fileName ?? a.file?.name,
                    bDoc?.fileName ?? b.file?.name,
                    sort === ImporterSort.FILE_NAME_ASC,
                );
            case ImporterSort.STATUS_ASC:
            case ImporterSort.STATUS_DESC:
                return compareNumbers(
                    getStatusSortPriority(a, context),
                    getStatusSortPriority(b, context),
                    sort === ImporterSort.STATUS_ASC,
                );
            case ImporterSort.BANK_ASC:
            case ImporterSort.BANK_DESC:
                return compareStrings(
                    getRowInstitutionName(a),
                    getRowInstitutionName(b),
                    sort === ImporterSort.BANK_ASC,
                );
            case ImporterSort.ACCOUNT_TYPE_ASC:
            case ImporterSort.ACCOUNT_TYPE_DESC:
                return compareStrings(
                    getRowAccountType(a),
                    getRowAccountType(b),
                    sort === ImporterSort.ACCOUNT_TYPE_ASC,
                );
            case ImporterSort.PERIOD_START_ASC:
            case ImporterSort.PERIOD_START_DESC:
                return compareDates(
                    aDoc?.openingDate,
                    bDoc?.openingDate,
                    sort === ImporterSort.PERIOD_START_ASC,
                );
            case ImporterSort.PERIOD_END_ASC:
            case ImporterSort.PERIOD_END_DESC:
                return compareDates(
                    aDoc?.closingDate,
                    bDoc?.closingDate,
                    sort === ImporterSort.PERIOD_END_ASC,
                );
            case ImporterSort.TRANSACTIONS_ASC:
            case ImporterSort.TRANSACTIONS_DESC:
                return compareNumbers(
                    aDoc?.numTransactions,
                    bDoc?.numTransactions,
                    sort === ImporterSort.TRANSACTIONS_ASC,
                );
            default:
                return 0;
        }
    });
}
